<template>
	<div class="teaming-layout bifurcated-body">
		<header class="bifurcated-body__header">
			<TeamingSegmentedButton
				:active-segment-name="selectedPanel"
				left-segment-name="left-panel"
				right-segment-name="right-panel"
				@change="$emit( `panelSelected`, $event )"
			>
				<template #left-segment>
					<span class="bifurcated-body__segmented-button-text">
						<slot name="left-title"></slot>
					</span>
				</template>
				<template #right-segment>
					<span class="bifurcated-body__segmented-button-text">
						<slot name="right-title"></slot>
					</span>
				</template>
			</TeamingSegmentedButton>
		</header>
		<main class="bifurcated-body__main">
			<section class="bifurcated-body__column bifurcated-body--left" :class="leftColumnClasses">
				<TranslucentOverlay v-if="disabled" />
				<header class="bifurcated-body__panel-header bifurcated-body__panel-header--left">
					<h2 class="bifurcated-body__panel-title bifurcated-body__panel-title--left">
						<slot name="left-title"></slot>
					</h2>
					<div class="bifurcated-body__panel-buttons--left" v-if="$slots[ `left-buttons` ]">
						<slot name="left-buttons"></slot>
					</div>
				</header>
				<section v-if="$slots[ `left-hint` ]" class="bifurcated-body__panel-hint bifurcated-body__panel-hint--left">
					<slot name="left-hint"></slot>
				</section>
				<main>
					<slot name="left-panel"></slot>
				</main>
			</section>
			<section class="bifurcated-body__column bifurcated-body--right" :class="rightColumnClasses">
				<TranslucentOverlay v-if="disabled" />
				<header class="bifurcated-body__panel-header bifurcated-body__panel-header--right">
					<h2 class="bifurcated-body__panel-title bifurcated-body__panel-title--right">
						<slot name="right-title"></slot>
					</h2>
					<div class="bifurcated-body__panel-buttons--right" v-if="$slots[ `right-buttons` ]">
						<slot name="right-buttons"></slot>
					</div>
				</header>
				<section v-if="$slots[ `right-hint` ]" class="bifurcated-body__panel-hint bifurcated-body__panel-hint--left">
					<slot name="left-hint"></slot>
				</section>
				<main>
					<slot name="right-panel"></slot>
				</main>
			</section>
		</main>
	</div>
</template>

<script>
import TeamingSegmentedButton from "../components/buttons/TeamingSegmentedButton.vue";
import TranslucentOverlay from "../components/TranslucentOverlay";
export default {
	name: "BifurcatedBody",
	emits: [ "panelSelected", ],
	components: {
		TeamingSegmentedButton,
		TranslucentOverlay,
	},
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		selectedPanel: {
			type: String,
			required: true,
			validator( value ) {
				return [ "left-panel", "right-panel", ].includes( value );
			},
		},
	},
	computed: {
		leftColumnClasses() {
			return {
				"bifurcated-body__column--visible": this.selectedPanel === "left-panel",
			};
		},
		rightColumnClasses() {
			return {
				"bifurcated-body__column--visible": this.selectedPanel === "right-panel",
			};
		},
	},
};
</script>

<style lang="scss" scoped>
$bifurcated-body-title-color: $color2-500;
$bifurcated-body-border-color: $color1-200;

.bifurcated-body {
	// dock to the sublayout body; content within the details
	// will scroll, but toolbar remains fixed
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	width: 100%;

	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;

	padding: 1rem;

	@include for-size('md') {
		gap: 1.5rem;
		padding: 2rem;
	}
}

.bifurcated-body__main {
	display: flex;
	flex-direction: row;
	justify-content: space-evenly;
	flex: 1;
}

.bifurcated-body__column {
	display: none;
	flex-direction: column;
	justify-content: flex-start;

	position: relative;
	width: 100%;

	&--visible {
		display: flex;
	}

	main {
		flex: 1;
		position: relative;
		padding-top: 1rem;
	}

	@include for-size('md') {
		display: flex;
		width: 50%;

		&:first-of-type {
			border-right: 0.0625rem solid $bifurcated-body-border-color;
			padding-right: 2rem;
		}

		&:last-of-type {
			padding-left: 2rem;
		}

		main {
			padding-top: 1.5rem;
		}
	}
}

.bifurcated-body__header {
	@include for-size('md') {
		display: none;
	}
}

.bifurcated-body__segmented-button-text {
	text-transform: capitalize;
}

.bifurcated-body__panel-title {
	display: none;
	text-align: left;
	@include font-and-line-for(1);
	font-weight: 600;
	color: $bifurcated-body-title-color;

	@include for-size('md') {
		display: block;
	}
}

.bifurcated-body__panel-header {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
}

.bifurcated-body__panel-buttons--right,
.bifurcated-body__panel-buttons--left {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	width: 100%;

	@include for-size('md') {
		width: auto;
	}
}

.bifurcated-body__panel-hint {
	display: none;
	flex-direction: row;
	justify-content: flex-start;

	@include font-and-line-for(-1);
	font-weight: 450;
	color: $teaming-hint-color;

	@include for-size('md') {
		display: flex;
	}
}
</style>
